'use client';

import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import { CurrencyIcon } from '@kit/ui/currency-icon';

export interface AddonSizeOption {
  /** The size's own add-on id — what gets selected and sold. */
  id: string;
  label: string;
  price: number;
}

interface AddonSizePickerProps<T extends AddonSizeOption> {
  /** Product name, for accessible labels. */
  productName: string;
  sizes: T[];
  /** Selected quantity per size id (missing / 0 = not selected). */
  quantities: ReadonlyMap<string, number>;
  /** qty 0 deselects the size. */
  onChange: (size: T, quantity: number) => void;
  /** Sizes whose quantity cannot go below their current value (e.g. paid). */
  lockedSizeIds?: ReadonlySet<string>;
  disabled?: boolean;
  freeAsCheckbox?: boolean;
}

/**
 * Multi-select size chips with per-size quantity: S×2, M×1, L×3 in one go.
 * Tapping an unselected chip adds one; the steppers adjust it; 0 removes it.
 * Shared by the /book extras list, the calendar add-on picker and the POS.
 */
export function AddonSizePicker<T extends AddonSizeOption>({
  productName,
  sizes,
  quantities,
  onChange,
  lockedSizeIds,
  disabled,
  freeAsCheckbox = false,
}: AddonSizePickerProps<T>) {
  const { t } = useTranslation('addons');

  return (
    <div className="flex flex-wrap gap-2" data-test="addon-size-picker">
      {sizes.map((size) => {
        const quantity = quantities.get(size.id) ?? 0;
        const name = `${productName} — ${size.label}`;

        if (freeAsCheckbox && size.price === 0) {
          return (
            <label
              key={size.id}
              className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-2.5 text-xs"
              data-test={`addon-size-${size.label}`}
            >
              <Checkbox
                aria-label={name}
                checked={quantity > 0}
                disabled={disabled || (quantity > 0 && lockedSizeIds?.has(size.id))}
                onCheckedChange={(checked) => onChange(size, checked === true ? 1 : 0)}
              />
              <span className="font-medium">{size.label}</span>
              <span className="text-muted-foreground">{t('bookings:free')}</span>
              {quantity > 1 && <span>×{quantity}</span>}
            </label>
          );
        }

        if (quantity === 0) {
          return (
            <Button
              key={size.id}
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-2.5 text-xs"
              onClick={() => onChange(size, 1)}
              disabled={disabled}
              aria-label={t('sizePicker.add', { name })}
              data-test={`addon-size-${size.label}`}
            >
              <span className="font-medium">{size.label}</span>
              <span className="text-muted-foreground inline-flex items-baseline gap-0.5">
                <CurrencyIcon className="h-2.5 w-2.5 self-center" />
                {size.price.toFixed(2)}
              </span>
            </Button>
          );
        }

        const locked = lockedSizeIds?.has(size.id) ?? false;

        return (
          <div
            key={size.id}
            className="border-primary flex h-8 items-center gap-1 rounded-md border px-1"
            data-test={`addon-size-${size.label}`}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onChange(size, quantity - 1)}
              disabled={disabled || locked}
              aria-label={t('decreaseQuantity', { name })}
            >
              <Minus className="h-3 w-3" />
            </Button>
            <span
              className="min-w-10 text-center text-xs font-medium"
              aria-live="polite"
              aria-label={t('quantityLabel', { count: quantity, name })}
            >
              {size.label} ×{quantity}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onChange(size, quantity + 1)}
              disabled={disabled}
              aria-label={t('increaseQuantity', { name })}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
